import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { EMPTY, catchError, firstValueFrom, throwError } from 'rxjs';

import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { stripQueryParams } from '../util/request-auth.util';

interface IdentityVerificationSession {
  provider?: string | null;
  launchMode?: string | null;
  sessionId?: string | null;
  launchUrl?: string | null;
}

interface IdentityVerificationLaunchResult {
  launchMode: 'embedded' | 'redirect';
  session: IdentityVerificationSession;
}

interface IdentityVerificationResponse {
  success?: number;
  data?: IdentityVerificationSession | null;
  message?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  getUserDetail(
    params: HttpParams = new HttpParams(),
    withoutGlobalLoader = false
  ) {
    if (!this.authService.getToken()) {
      return EMPTY;
    }

    const sanitizedParams = stripQueryParams(params, ['userId']);
    const request = withoutGlobalLoader
      ? this.apiService.getWithoutGlobalLoader(
          'rest/user/getUserDetail',
          sanitizedParams
        )
      : this.apiService.get('rest/user/getUserDetail', sanitizedParams);

    return request.pipe(
      catchError((error) => {
        if (error?.status === 401) {
          this.authService.removeCookie();
          return EMPTY;
        }

        return throwError(() => error);
      })
    );
  }

  getCountries(withoutGlobalLoader = false) {
    return withoutGlobalLoader
      ? this.apiService.getWithoutGlobalLoader('rest/user/countries')
      : this.apiService.get('rest/user/countries');
  }

  constructor(
    private apiService: ApiService,
    private authService: AuthService
  ) {}

  // for htting tha backedn api for logging in
  login(request: object, rememberMe = false) {
    return this.apiService.postWithCredentials('rest/auth/authenticate', {
      ...request,
      rememberMe,
    });
  }

  createIdentityVerificationSession(request: object) {
    return this.apiService.postWithoutGlobalLoader(
      'rest/user/identityVerification/session',
      {
        ...request,
        initiatorDevice: this.getIdentityVerificationInitiatorDevice(),
      }
    );
  }

  private getIdentityVerificationInitiatorDevice(): 'desktop' | 'mobile' {
    if (typeof navigator === 'undefined') {
      return 'desktop';
    }

    const navigatorWithClientHints = navigator as Navigator & {
      userAgentData?: { mobile?: boolean };
    };
    if (navigatorWithClientHints.userAgentData?.mobile === true) {
      return 'mobile';
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const iPadDesktopMode =
      userAgent.includes('macintosh') && navigator.maxTouchPoints > 1;
    return /mobi|android|iphone|ipad|ipod/.test(userAgent) || iPadDesktopMode
      ? 'mobile'
      : 'desktop';
  }

  private resolveIdentityVerificationLaunchMode(
    session: IdentityVerificationSession
  ): 'embedded' | 'redirect' {
    return session?.launchMode === 'embedded' ? 'embedded' : 'redirect';
  }

  private extractIdentityVerificationErrorMessage(
    error: unknown,
    depth = 0
  ): string | null {
    if (error == null || depth > 4) {
      return null;
    }

    if (error instanceof HttpErrorResponse) {
      return (
        this.extractIdentityVerificationErrorMessage(error.error, depth + 1) ||
        this.extractIdentityVerificationErrorMessage(error.message, depth + 1)
      );
    }

    if (typeof error === 'string') {
      const message = error.trim();
      if (!message) {
        return null;
      }

      try {
        return (
          this.extractIdentityVerificationErrorMessage(
            JSON.parse(message),
            depth + 1
          ) || message
        );
      } catch {
        return message;
      }
    }

    if (error instanceof Error && error.message.trim()) {
      return error.message.trim();
    }

    if (error && typeof error === 'object') {
      const objectError = error as {
        message?: unknown;
        error?: unknown;
      };

      return (
        this.extractIdentityVerificationErrorMessage(
          objectError.message,
          depth + 1
        ) ||
        this.extractIdentityVerificationErrorMessage(
          objectError.error,
          depth + 1
        )
      );
    }

    return null;
  }

  getIdentityVerificationErrorMessage(
    error: unknown,
    fallback = 'Unable to launch identity verification. Please try again.'
  ) {
    return this.extractIdentityVerificationErrorMessage(error) || fallback;
  }

  showIdentityVerificationError(
    error: unknown,
    fallback = 'Unable to launch identity verification. Please try again.'
  ) {
    const message = this.getIdentityVerificationErrorMessage(error, fallback);
    if (typeof window !== 'undefined' && typeof window.alert === 'function') {
      setTimeout(() => {
        window.alert(message);
      }, 0);
    }
    return message;
  }

  async openIdentityVerificationSession(
    session: IdentityVerificationSession
  ): Promise<IdentityVerificationLaunchResult> {
    const provider = (session?.provider || 'didit').trim().toLowerCase();
    const launchUrl = session?.launchUrl?.trim();
    const launchMode = this.resolveIdentityVerificationLaunchMode(session);

    if (!launchUrl) {
      throw new Error('Identity verification session is missing a launch URL');
    }

    if (provider !== 'didit' || launchMode !== 'redirect') {
      throw new Error(
        `Unsupported identity verification launch configuration: ${provider}/${launchMode}`
      );
    }

    window.location.assign(launchUrl);
    return {
      launchMode,
      session,
    };
  }

  async createAndLaunchIdentityVerificationSession(
    request: object
  ): Promise<IdentityVerificationLaunchResult> {
    const response = (await firstValueFrom(
      this.createIdentityVerificationSession(request)
    )) as IdentityVerificationResponse;

    if (response?.success !== 0 || !response?.data?.launchUrl) {
      throw new Error(
        this.getIdentityVerificationErrorMessage(
          response?.message,
          'Unable to start identity verification right now.'
        )
      );
    }

    return this.openIdentityVerificationSession(response.data);
  }

  //for hitting the addUser Api for adding partial detials from the first page of signup
  signUp(request: object) {
    return this.apiService.post('rest/user/', request);
  }

  //for hitting the addUser Api for adding partial detials from the first page of signup
  editUser(request: object) {
    return this.apiService.put('rest/user/', request);
  }

  //For hitting API sendResetPasswordLink for sending a reset password link
  forgotPwd(params: HttpParams) {
    return this.apiService.postParams(
      'rest/user/sendResetPasswordLink',
      params
    );
  }

  //For hitting this API resetPassword for when user clicks on the link sent to him for resetting the password
  resetPwd(params: HttpParams) {
    return this.apiService.postParams('rest/user/resetPassword', params);
  }

  //for hitting the api for  verifying activation code
  activationCodeVerify(params: HttpParams) {
    return this.apiService.postParams('rest/user/activationCodeVerify', params);
  }

  resendActivationCode(params: HttpParams) {
    return this.apiService.postParams('rest/user/resendActivationCode', params);
  }

  // for hitting the api for setting and storing username and password for the first time.
  activateProfile(request: object) {
    return this.apiService.post('rest/user/setPassword', request);
  }

  //for checking if the username already exists.
  checkExistingUsername(params: HttpParams) {
    return this.apiService.postParams(
      'rest/user/checkExistingUserName',
      params
    );
  }

  //for checking if the phone number already exists.
  checkExistingPhoneNumber(params: HttpParams) {
    return this.apiService.postParams(
      'rest/user/checkExistingPhoneNumber',
      params
    );
  }

  //for sending OTP
  sendOTP(request: object) {
    return this.apiService.post('rest/user/sendOTP', request);
  }

  //for verifying OTP
  verifyOTP(params: HttpParams) {
    return this.apiService.postParams('rest/user/otpVerify', params);
  }

  //For deleting user account
  deleteUserAccount(params: HttpParams = new HttpParams()) {
    return this.apiService.delete(
      'rest/user/',
      stripQueryParams(params, ['userId'])
    );
  }
  // For fetching text verified user details
  fetchTextVerifiedUser() {
    return this.apiService.get('rest/user/textVerified');
  }

  // For updating user verification details
  updateUserVerificationDetails(params: HttpParams) {
    return this.apiService.putParams('rest/user/verificationDetails', params);
  }

  //for unsubscribing mail
  unsubscribeMail(params: HttpParams) {
    return this.apiService.postParams('rest/user/unsubscribe', params);
  }

  fetchUserMailSubscription(params: HttpParams) {
    return this.apiService.get('rest/user/unsubscribe', params);
  }

  // for sending bug report/feedback of user
  sendBugReportOrFeedback(request: object) {
    return this.apiService.post('rest/user/reportABugOrFeedback', request);
  }

  // For updating email or phone no
  updateEmailOrPhoneNumber(params: HttpParams) {
    return this.apiService.putParams(
      'rest/user/emailOrPhoneNumber',
      stripQueryParams(params, ['userId'])
    );
  }

  // For fetching timezone lis
  fetchTimeZones() {
    return this.apiService.get('rest/user/timezone');
  }

  // For updating virtual event settings
  virtualEventNotification(params: HttpParams) {
    return this.apiService.postParams(
      'rest/user/virtualEventNotification',
      params
    );
  }

  // For updating email notification settings
  updateEmailNotificationSetting(isEmailNotified: boolean) {
    const params = new HttpParams().set(
      'isEmailNotified',
      isEmailNotified.toString()
    );

    return this.apiService.putParams(
      'rest/user/emailNotificationSetting',
      params
    );
  }

  // For subscribe/unsubscribe politicians
  subscribeOrUnsubscribePoliticians(params: HttpParams) {
    return this.apiService.postParams(
      'rest/user/subscribeOrUnsubscribePoliticians',
      params
    );
  }

  // To submit report abuse
  reportAbuse(formData: FormData) {
    return this.apiService.postFile('rest/user/reportAbuse', formData);
  }

  // To submit issue
  troubleSignup(request: object) {
    return this.apiService.post('rest/user/troubleSignup', request);
  }

  // To submit help form
  submitHelpForm(request: object) {
    return this.apiService.post('rest/user/helpform', request);
  }
}
